import React, { useCallback } from 'react';
import { forTemplate } from 'app/common/lib/util/i18n';
import cx from 'classnames';
import {
  IconButton,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import { IconName, IconColor } from 'app/common/components/controls';
import { CreateCardFromTemplatePopover } from 'app/src/components/CardTemplatesPopover';
import Backbone from 'app/common/vendor/backbone';
import { useCreateCardFromTemplateMutation } from '@trello/graphql/generated';
import styles from './TemplateCardBanner.less';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';
import { usePopover, Popover } from '@trello/nachos';

const format = forTemplate('card_detail');

interface TemplateCardBannerProps {
  idCard: string;
  editable?: boolean;
  hasCover?: boolean;
  hasStickers?: boolean;
}

export const CARD_BACK_CREATE_FROM_TEMPLATE_NAME = Symbol(
  'CARD_BACK_CREATE_FROM_TEMPLATE',
);

export const TemplateCardBanner: React.FunctionComponent<TemplateCardBannerProps> = ({
  idCard,
  editable,
  hasCover,
  hasStickers,
}) => {
  const sendGASEvent = featureFlagClient.get(
    'dataeng.gasv3-event-tracking',
    false,
  );

  const onShow = useCallback(() => {
    trackUe({
      category: Noun.CARD_TEMPLATES,
      verb: Verb.OPENS,
      directObj: Noun.CREATE_CARD_FROM_TEMPLATE_MENU,
      context: {
        entry: Noun.CARD_BACK,
      },
    });
    if (sendGASEvent) {
      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'createCardFromTemplateButton',
        source: 'cardDetailScreen',
        attributes: {
          isTemplate: true,
        },
      });
    }
  }, [sendGASEvent]);

  const { toggle, hide, popoverProps, triggerRef } = usePopover<
    HTMLButtonElement
  >({ onShow });

  const [createCardMutation] = useCreateCardFromTemplateMutation();

  return (
    <div
      className={cx(styles.templateCardBanner, {
        [styles.hasCover]: hasCover,
        [styles.hasStickers]: hasStickers,
      })}
    >
      <span className={styles.templateCardBannerImage} />
      <h3 className={styles.templateCardBannerText}>
        {format('this-is-a-template-card')}
      </h3>
      {editable ? (
        <IconButton
          className={styles.templateCardBannerButton}
          icon={IconName.TemplateCreate}
          color={IconColor.Light}
          buttonType={ButtonTypes.PRIMARY}
          ref={triggerRef}
          onClick={toggle}
        >
          {format('create-card-from-template')}
        </IconButton>
      ) : null}
      <Popover {...popoverProps} title={format('create-card-from-template')}>
        <CreateCardFromTemplatePopover
          idCard={idCard}
          showLists
          hideEditButton
          createCard={async (name, idCardSource, keepFromSource, idList) => {
            if (!idList) {
              return;
            }

            if (sendGASEvent) {
              Analytics.sendUIEvent({
                action: 'clicked',
                actionSubject: 'button',
                actionSubjectId: 'createCardButton',
                source: 'createCardFromTemplateInlineDialog',
              });
            }

            const { data } = await createCardMutation({
              variables: {
                idCardSource,
                idList,
                name,
                keepFromSource,
              },
            });

            const card = data?.copyCard;

            if (card) {
              trackUe({
                category: Noun.CARD_TEMPLATES,
                verb: Verb.COMPLETES,
                directObj: Noun.CREATE_CARD_FROM_TEMPLATE_MENU,
                context: {
                  entry: Noun.CARD_BACK,
                },
              });
              if (sendGASEvent) {
                Analytics.sendTrackEvent({
                  action: 'created',
                  actionSubject: 'card',
                  source: 'createCardFromTemplateInlineDialog',
                });
              }
              hide();
              Backbone.history.navigate(`/c/${card.shortLink}`, {
                trigger: true,
              });
            }
          }}
        />
      </Popover>
    </div>
  );
};
